import { baseApiAuthenticated } from '@/api/v2/index';
import { buildSearchParams, urlBuilder } from '@/api/utils';
import { CreateLiftOperator, LiftOperator } from '@/models/liftoperator';
import {
    AggregatedTicket,
    AggregatedTicketSearchParams,
    convertAggregatedTicketSearchParamsToBackend,
} from '@/models/aggregatedTicket';
import { SalesStatistic, StatisticType } from '@/models/statistic';

type GetStatisticsParams = { statisticTypes?: StatisticType[] };

const liftOperatorUrls = {
    liftOperator: urlBuilder('liftoperators', `liftoperators`),
    aggregatedTicket: urlBuilder('order', 'liftoperators/tickets-aggregated'),
    liftOperatorStatistics: urlBuilder('order', 'liftoperators/statistics'),
};

export const liftOperatorApi = baseApiAuthenticated
    .enhanceEndpoints({ addTagTypes: ['LIFTOPERATORS'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getLiftOperators: builder.query<LiftOperator[], void>({
                query: () => liftOperatorUrls.liftOperator,
                transformResponse: (response: { content: LiftOperator[] }) => {
                    return response.content;
                },
                providesTags: ['LIFTOPERATORS'],
            }),

            getAllLiftOperators: builder.query<LiftOperator[], void>({
                query: () => `${liftOperatorUrls.liftOperator}?size=1000`,
                transformResponse: (response: { content: LiftOperator[] }) => {
                    return response.content;
                },
                providesTags: ['LIFTOPERATORS'],
            }),

            getLiftOperator: builder.query<
                LiftOperator,
                {
                    liftOperatorId: string;
                }
            >({
                query: ({ liftOperatorId }) =>
                    `${liftOperatorUrls.liftOperator}/${liftOperatorId}`,
            }),

            putLiftOperator: builder.mutation<LiftOperator, LiftOperator>({
                query: (liftOperator) => ({
                    method: 'PUT',
                    url: `${liftOperatorUrls.liftOperator}/${liftOperator.id}`,
                    body: liftOperator,
                }),
                invalidatesTags: ['LIFTOPERATORS'],
            }),

            postLiftOperator: builder.mutation<
                LiftOperator,
                CreateLiftOperator
            >({
                query: (liftOperator) => ({
                    method: 'POST',
                    url: `${liftOperatorUrls.liftOperator}`,
                    body: liftOperator,
                }),
                invalidatesTags: ['LIFTOPERATORS'],
            }),

            putLiftOperatorImage: builder.mutation<
                LiftOperator,
                {
                    liftOperatorId: string;
                    image: File;
                    type: 'headerImage' | 'logo';
                }
            >({
                query: ({ liftOperatorId, image, type }) => ({
                    method: 'PUT',
                    url: `${liftOperatorUrls.liftOperator}/${liftOperatorId}/${type}`,
                    body: image,
                }),
                invalidatesTags: ['LIFTOPERATORS'],
            }),

            deleteLiftOperatorImage: builder.mutation<
                LiftOperator,
                { liftOperatorId: string; type: 'headerImage' | 'logo' }
            >({
                query: ({ liftOperatorId, type }) => ({
                    method: 'DELETE',
                    url: `${liftOperatorUrls.liftOperator}/${liftOperatorId}/${type}`,
                }),
                invalidatesTags: ['LIFTOPERATORS'],
            }),

            postLiftOperatorLogo: builder.mutation<
                LiftOperator,
                CreateLiftOperator
            >({
                query: (liftOperator) => ({
                    method: 'POST',
                    url: `${liftOperatorUrls.liftOperator}`,
                    body: liftOperator,
                }),
                invalidatesTags: ['LIFTOPERATORS'],
            }),

            getAggregatedTickets: builder.query<
                AggregatedTicket[],
                AggregatedTicketSearchParams | void
            >({
                query: (params: AggregatedTicketSearchParams = {}) =>
                    `${liftOperatorUrls.aggregatedTicket}${buildSearchParams(
                        convertAggregatedTicketSearchParamsToBackend(params)
                    )}`,
                transformResponse: (response: {
                    aggregatedTickets: AggregatedTicket[];
                }) => {
                    return response.aggregatedTickets;
                },
            }),

            getStatistics: builder.query<
                SalesStatistic,
                GetStatisticsParams | void
            >({
                query: (params: GetStatisticsParams = {}) =>
                    `${
                        liftOperatorUrls.liftOperatorStatistics
                    }${buildSearchParams(params)}`,
            }),
        }),
    });

export const {
    useGetLiftOperatorsQuery,
    useGetAllLiftOperatorsQuery,
    useLazyGetAggregatedTicketsQuery,
    usePutLiftOperatorMutation,
    useGetLiftOperatorQuery,
    usePostLiftOperatorMutation,
    usePutLiftOperatorImageMutation,
    useDeleteLiftOperatorImageMutation,
} = liftOperatorApi;
